import React from 'react';
import TooltipTrigger, { TooltipArg, TriggerTypes } from 'react-popper-tooltip';
import styled from 'styled-components';
import { IHasTestData } from '../../test-utilities/IHasTestData';

export interface ITooltipProps {
  tooltipContent: React.ReactNode;
  placement?: 'auto' | 'bottom' | 'top' | 'right' | 'left';
  tooltipShown?: boolean;
  trigger?: TriggerTypes;
  offset?: string;
  triggerClassname?: string;
  children?: React.ReactNode;
}

export const Tooltip: React.FC<ITooltipProps> = (props) => (
  <TooltipTrigger
    {...props}
    placement={props.placement}
    trigger={props.trigger || 'click'}
    tooltip={(ref) => <TooltipContent content={props.tooltipContent} offset={props.offset || ''} {...ref} />}
  >
    {(ref) => <Trigger {...ref} content={props.children} triggerClassName={props.triggerClassname} />}
  </TooltipTrigger>
);

interface ITooltipContent extends TooltipArg, IHasTestData {
  content: React.ReactNode;
  offset: string;
}

const TooltipContent: React.FC<ITooltipContent> = ({
  arrowRef,
  tooltipRef,
  getArrowProps,
  getTooltipProps,
  placement,
  content,
  offset,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
}) => (
  <TooltipWrapper
    offset={offset}
    data-test-class={dataTestClass || 'tooltip'}
    data-test-id={dataTestId || `tooltip-${placement}`}
  >
    <div
      {...getTooltipProps({
        ref: tooltipRef,
        className: 'tooltip-container',
        'data-placement': placement,
      })}
    >
      <div
        {...getArrowProps({
          ref: arrowRef,
          className: 'tooltip-arrow',
          'data-placement': placement,
          /* your props here */
        })}
      />
      {content}
    </div>
  </TooltipWrapper>
);

const Trigger: React.FC<any> = ({ getTriggerProps, triggerRef, content, triggerClassName }) => {
  return (
    <div {...getTriggerProps({ ref: triggerRef, className: `trigger ${triggerClassName ?? 'inline-block'}` })}>
      {content}
    </div>
  );
};

interface ITooltipWrapper {
  offset: string;
}

//TODO: Fix CSS import in Rails
const TooltipWrapper = styled.div<ITooltipWrapper>`
  .tooltip-container {
    background-color: white;
    border-radius: 3px;
    border: 1px solid ${(props) => props.theme.colors.defaultContentBorder};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    padding: ${(props) => props.theme.spacing.spacing4};
    transition: opacity 0.3s;
    z-index: 9999;
  }

  .tooltip-container[data-placement*='right'] {
    margin-left: ${(props) => props.offset};
  }

  .tooltip-container[data-placement*='left'] {
    margin-right: ${(props) => props.offset};
  }

  .tooltip-container[data-placement*='top'] {
    margin-bottom: ${(props) => props.offset};
  }

  .tooltip-container[data-placement*='bottom'] {
    margin-top: ${(props) => props.offset};
  }

  .tooltip-arrow {
    height: 1rem;
    position: absolute;
    width: 1rem;
  }

  .tooltip-arrow::before {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    width: 0;
  }

  .tooltip-arrow::after {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    position: absolute;
    width: 0;
  }

  .tooltip-arrow[data-placement*='bottom'] {
    height: 1rem;
    left: 0;
    margin-top: -0.4rem;
    top: 0;
    width: 1rem;
  }

  .tooltip-arrow[data-placement*='bottom']::before {
    border-color: transparent transparent ${(props) => props.theme.colors.defaultContentBorder} transparent;
    border-width: 0 0.5rem 0.4rem 0.5rem;
    position: absolute;
    top: -1px;
  }

  .tooltip-arrow[data-placement*='bottom']::after {
    border-color: transparent transparent white transparent;
    border-width: 0 0.5rem 0.4rem 0.5rem;
  }

  .tooltip-arrow[data-placement*='top'] {
    bottom: 0;
    height: 1rem;
    left: 0;
    margin-bottom: -1rem;
    width: 1rem;
  }

  .tooltip-arrow[data-placement*='top']::before {
    border-color: ${(props) => props.theme.colors.defaultContentBorder} transparent transparent transparent;
    border-width: 0.4rem 0.5rem 0 0.5rem;
    position: absolute;
    top: 1px;
  }

  .tooltip-arrow[data-placement*='top']::after {
    border-color: white transparent transparent transparent;
    border-width: 0.4rem 0.5rem 0 0.5rem;
  }

  .tooltip-arrow[data-placement*='right'] {
    height: 1rem;
    left: 0;
    margin-left: -0.7rem;
    width: 1rem;
  }

  .tooltip-arrow[data-placement*='right']::before {
    border-color: transparent ${(props) => props.theme.colors.defaultContentBorder} transparent transparent;
    border-width: 0.5rem 0.4rem 0.5rem 0;
  }

  .tooltip-arrow[data-placement*='right']::after {
    border-color: transparent white transparent transparent;
    border-width: 0.5rem 0.4rem 0.5rem 0;
    left: 6px;
    top: 0;
  }

  .tooltip-arrow[data-placement*='left'] {
    height: 1rem;
    margin-right: -0.7rem;
    right: 0;
    width: 1rem;
  }

  .tooltip-arrow[data-placement*='left']::before {
    border-color: transparent transparent transparent ${(props) => props.theme.colors.defaultContentBorder};
    border-width: 0.5rem 0 0.5rem 0.4em;
  }

  .tooltip-arrow[data-placement*='left']::after {
    border-color: transparent transparent transparent white;
    border-width: 0.5rem 0 0.5rem 0.4em;
    left: 3px;
    top: 0;
  }
`;
